/**
 * `<RewindScrubber/>` — the time scrubber (the hero interaction of B3.3).
 *
 * Issue #764 (Phase 3). A bottom-docked timeline: the "REWIND" label, the
 * earliest-date marker, a draggable handle running left→right to "now," and
 * the selected day shown prominently under the handle. Dragging the handle
 * changes the chosen day; the parent refetches the as-of tree and re-renders
 * the canvas as it stood then. A "play" control walks the handle forward day
 * by day for a calm, cinematic replay; reaching "now" snaps back to live.
 *
 * Faithful to `B3-3-rewind.svg`: the soft-mint bottom panel, the indigo track
 * fill + handle (#4F46E5), the day labels at each end, the floating date
 * under the handle, and the "▶ play" affordance. The interaction is built on
 * a native range input (keyboard-accessible + smooth) styled to the gallery,
 * with an indigo fill rendered behind it.
 *
 * Editorial revamp (#1043, Wave 2): theme-aware via `useMapTheme()`.
 *   - LEGACY (flag off): the shipped soft-mint panel + indigo track/handle.
 *     Byte-identical to today.
 *   - EDITORIAL (flag on): the wireframe `.t-scrubber` — a warm `--bg-2` bar,
 *     a `c.ink` fill + knob, `c.ink-4` ticks, mono ticklabels, and a sage
 *     "now" end-tick. RE-SKIN, not rebuild: the native range input, every
 *     `data-testid`, the day labels + floating selected date, and the
 *     play-walk behaviour are all unchanged.
 *
 * Pure presentational: it owns no data. The parent holds the chosen day
 * offset + the timeline; this component only renders + emits changes.
 *
 * Brand firewall (§A9): the axis is the PLAN's trajectory in days — never a
 * per-person activity timeline. All copy is factual.
 */

import { useEffect, useRef } from "react";

import { useMapTheme } from "../theme/MapThemeContext";
import { PALETTE } from "../tree/palette";
import { type RewindTimeline, dayOffsetToDate, formatDayLabel, isAtNow } from "./rewindTimeline";

const INDIGO = "#4F46E5";
const INDIGO_TRACK = "rgba(79,70,229,0.30)";

export interface RewindScrubberProps {
  timeline: RewindTimeline;
  /** The currently-selected whole-day offset (0 … totalDays). */
  dayOffset: number;
  /** Emit a new day offset (drag / keyboard / play tick). */
  onChange: (dayOffset: number) => void;
  /** Whether the play-walk is running (drives the play/pause glyph). */
  playing: boolean;
  /** Toggle the play-walk. */
  onTogglePlay: () => void;
}

/** One-day-per-tick forward walk; slow enough to read each day. */
const PLAY_INTERVAL_MS = 900;

export function RewindScrubber(props: RewindScrubberProps) {
  const { timeline, dayOffset, onChange, playing, onTogglePlay } = props;
  const { totalDays } = timeline;

  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  // The scrubber's accent: editorial = warm `ink`; legacy = the indigo handle.
  const accent = editorial ? c.ink : INDIGO;
  const trackFill = editorial ? c.ink : INDIGO_TRACK;
  // The "now" end-tick reads sage (done) in editorial, the indigo accent otherwise.
  const nowTick = editorial ? c.rollupFill : INDIGO;

  const selectedDate = dayOffsetToDate(timeline, dayOffset);
  const atNow = isAtNow(timeline, dayOffset);
  // Percent of the track filled to the handle (guard a zero-span timeline).
  const pct = totalDays > 0 ? (dayOffset / totalDays) * 100 : 100;

  // The play-walk: advance one day every tick; stop (and let the parent snap
  // to live) on reaching the end. A ref-free effect keyed on `playing` +
  // `dayOffset` re-arms the timer for each step.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onTogglePlayRef = useRef(onTogglePlay);
  onTogglePlayRef.current = onTogglePlay;
  useEffect(() => {
    if (!playing) return;
    if (dayOffset >= totalDays) {
      onTogglePlayRef.current();
      return;
    }
    const id = window.setTimeout(() => {
      onChangeRef.current(Math.min(totalDays, dayOffset + 1));
    }, PLAY_INTERVAL_MS);
    return () => window.clearTimeout(id);
  }, [playing, dayOffset, totalDays]);

  return (
    <div
      data-testid="rewind-scrubber"
      className="flex items-center gap-4 border-t px-6 py-4"
      style={{
        borderColor: editorial ? c.rule : PALETTE.softBorder,
        backgroundColor: editorial ? c.bgRecess : PALETTE.cardBg,
      }}
    >
      {/* The "REWIND" label — a mono issue tag in editorial; the legacy bold
          tracking caps otherwise. */}
      {editorial ? (
        <span
          className="shrink-0 text-[10px] uppercase"
          style={{ fontFamily: theme.fonts.mono, letterSpacing: "0.08em", color: c.muted }}
        >
          REWIND
        </span>
      ) : (
        <span
          className="shrink-0 text-[11px] font-bold tracking-wide"
          style={{ color: PALETTE.muted }}
        >
          REWIND
        </span>
      )}

      {/* Earliest-day marker. */}
      <span
        className="shrink-0 text-[10px]"
        style={
          editorial ? { color: c.muted, fontFamily: theme.fonts.mono } : { color: PALETTE.faint }
        }
      >
        {formatDayLabel(timeline.from)}
      </span>

      {/* The track + handle. The fill sits behind a transparent native range
          input that owns the actual drag/keyboard interaction. */}
      <div className="relative flex-1">
        <div
          className="absolute left-0 right-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full"
          style={{ backgroundColor: editorial ? c.rollupTrack : PALETTE.connector }}
          aria-hidden
        />
        <div
          className="absolute left-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full"
          style={{ width: `${pct}%`, backgroundColor: trackFill }}
          aria-hidden
          data-testid="rewind-track-fill"
        />
        {/* Editorial "now" end-tick — a sage marker at the live end (`V_B33`).
            Legacy renders no extra tick (byte-identical). */}
        {editorial && (
          <div
            data-testid="rewind-now-tick"
            aria-hidden
            className="absolute top-1/2 h-3 w-px -translate-y-1/2"
            style={{ right: 0, backgroundColor: nowTick }}
          />
        )}
        <input
          type="range"
          data-testid="rewind-slider"
          min={0}
          max={Math.max(totalDays, 1)}
          step={1}
          value={dayOffset}
          aria-label="Rewind the map to a past day"
          aria-valuetext={atNow ? "now" : formatDayLabel(selectedDate)}
          onChange={(e) => onChange(Number(e.target.value))}
          className="rewind-range relative z-10 w-full cursor-pointer appearance-none bg-transparent"
        />
        {/* The selected day, floating under the handle. */}
        <div
          className="pointer-events-none absolute -bottom-5 -translate-x-1/2 whitespace-nowrap text-[10px] font-semibold"
          style={
            editorial
              ? { left: `${pct}%`, color: accent, fontFamily: theme.fonts.mono }
              : { left: `${pct}%`, color: INDIGO }
          }
          data-testid="rewind-selected-date"
        >
          {atNow ? "now" : formatDayLabel(selectedDate)}
        </div>
      </div>

      {/* "Now" marker. */}
      <span
        className="shrink-0 text-[10px]"
        style={
          editorial ? { color: c.muted, fontFamily: theme.fonts.mono } : { color: PALETTE.faint }
        }
      >
        {formatDayLabel(timeline.now)} (now)
      </span>

      {/* Play / pause — a calm forward walk through the days. */}
      <button
        type="button"
        data-testid="rewind-play"
        onClick={onTogglePlay}
        disabled={atNow}
        aria-label={playing ? "Pause the rewind walk" : "Play the rewind forward"}
        className="shrink-0 rounded-md border px-3 py-1 text-[11px] transition-colors enabled:hover:bg-slate-50 disabled:opacity-40"
        style={{
          borderColor: editorial ? c.rule2 : PALETTE.softBorder,
          color: editorial ? c.ink : PALETTE.slate,
        }}
      >
        {playing ? "❚❚ pause" : "▶ play"}
      </button>

      {/* Range thumb styling — scoped by the `.rewind-range` class so it never
          bleeds onto other sliders. Editorial paints an ink handle with a
          cream ring (the `.t-scrubber .knob`); legacy keeps the indigo handle. */}
      <style>{`
        .rewind-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          height: 18px;
          width: 18px;
          border-radius: 9999px;
          background: ${accent};
          border: 3px solid ${editorial ? c.canvas : "#ffffff"};
          box-shadow: 0 1px 4px ${editorial ? "rgba(26,22,18,0.30)" : "rgba(79,70,229,0.35)"};
          cursor: pointer;
        }
        .rewind-range::-moz-range-thumb {
          height: 18px;
          width: 18px;
          border-radius: 9999px;
          background: ${accent};
          border: 3px solid ${editorial ? c.canvas : "#ffffff"};
          box-shadow: 0 1px 4px ${editorial ? "rgba(26,22,18,0.30)" : "rgba(79,70,229,0.35)"};
          cursor: pointer;
        }
        .rewind-range:focus-visible::-webkit-slider-thumb {
          outline: 2px solid ${accent};
          outline-offset: 2px;
        }
      `}</style>
    </div>
  );
}
